import {
  CancelOutlined,
  CheckOutlined,
  DeleteOutlined,
} from '@mui/icons-material'
import {
  Box,
  Button,
  ButtonBaseActions,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Key } from 'utils/keyCodes'

type Props = {
  onDelete: () => void
  deleteConfirmationMessage?: string
  buttonType: 'button' | 'icon'
  title?: React.ReactNode
  color?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
}

export const DeleteButtonWithConfirmation = (props: Props) => {
  const { t } = useTranslation()

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)

  const confirmRef = React.useRef<HTMLDivElement>(null)

  const okButtonRef = React.useRef<ButtonBaseActions>(null)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === Key.Escape) {
        setIsDeleteConfirmationOpen(false)
        event.stopPropagation()
      }
    }

    confirmRef.current.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useLayoutEffect(() => {
    if (isDeleteConfirmationOpen) {
      setTimeout(() => {
        okButtonRef.current?.focusVisible()
        // needs to give time for the grow animation finish
      }, 120)
    }
  }, [isDeleteConfirmationOpen])

  useEffect(() => {
    const controller = new AbortController()

    document.addEventListener(
      'click',
      (e) => {
        if (
          isDeleteConfirmationOpen &&
          confirmRef.current &&
          !buttonConfirmationRef.current.contains(e.target as Node)
        ) {
          setIsDeleteConfirmationOpen(false)
        }
      },
      { signal: controller.signal },
    )

    return () => {
      controller.abort()
    }
  }, [isDeleteConfirmationOpen])

  const buttonConfirmationRef = React.useRef(null)

  const ButtonComponent = React.useMemo(() => {
    switch (props.buttonType) {
      case 'icon':
        return (
          <IconButton
            ref={buttonConfirmationRef}
            size="small"
            color={props.color || 'error'}
            onClick={(e) => {
              // e.stopPropagation()
              setIsDeleteConfirmationOpen((c) => !c)
            }}
          >
            <DeleteOutlined />
          </IconButton>
        )
      case 'button': {
        return (
          <Button
            size="small"
            ref={buttonConfirmationRef}
            color={props.color || 'error'}
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation()
              setIsDeleteConfirmationOpen((c) => !c)
            }}
          >
            {t('common:delete', 'delete')}
          </Button>
        )
      }
    }
  }, [props.buttonType, props.color, t])

  const boundingBox = buttonConfirmationRef.current?.getBoundingClientRect()

  return (
    <span>
      {props.title ? (
        <Tooltip title={props.title} arrow>
          <div>{ButtonComponent}</div>
        </Tooltip>
      ) : (
        <div>{ButtonComponent}</div>
      )}
      <div
        hidden={!isDeleteConfirmationOpen}
        style={{
          position: 'fixed',
          left: `calc(${boundingBox?.left}px - 46px)`,
          top: `calc(${boundingBox?.top}px - 4px)`,
          transform: `translateX(-63%)`,
          zIndex: '1000',
        }}
        ref={confirmRef}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            backgroundColor: (theme) => theme.palette.background.default,
            color: (theme) => theme.palette.text.primary,
            alignItems: 'center',
            overflow: 'visible',
            textOverflow: 'revert',
            padding: (theme) => theme.spacing(0, 1),
            boxShadow: (theme) => theme.shadows[1],
            borderRadius: '4px',
            border: '1px solid red',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body2"
              style={{
                width: '100%',
                whiteSpace: 'nowrap',
                textAnchor: 'end',
                color: 'inherit',
              }}
            >
              {props.deleteConfirmationMessage ||
                t(
                  'common:delete-confirmation',
                  'are you sure you want to delete this item?',
                )}
            </Typography>
            <IconButton
              onClick={props.onDelete}
              color="success"
              action={okButtonRef}
            >
              <CheckOutlined />
            </IconButton>
            <IconButton
              onClick={() => setIsDeleteConfirmationOpen(false)}
              color="error"
            >
              <CancelOutlined />
            </IconButton>
          </div>
        </Box>
      </div>
    </span>
  )
}
